import React, { FunctionComponent, useState, useMemo } from 'react'
import { Obj } from './index'
import { moneyFormatter } from '@/utils/helpers'
import useIntl from '@/utils/intl'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import Image from 'next/image'
import { useTheme } from '@/context/ThemeContext'
import { useMobiApi } from '@/services/mobi-api'
import { useToast } from '@/context/ToastContext'

interface Props {
  objs: Obj
  onConfirm: () => void
  onBack: () => void
  instanceId?: string
}

const OrderSummary: FunctionComponent<Props> = ({ objs, instanceId, onConfirm }) => {
  const { translate } = useIntl()
  const { theme } = useTheme()
  const [usageLimit, setUsageLimit] = useState(false)
  const [showPopup, setShowPopup] = useState(false)
  const [loading, setLoading] = useState(false)
  const { numberv2 } = useMobiApi()
  const { showError } = useToast()

  const packageInfo = useMemo(() => {
    if ((objs?.choosenInfo?.choosenPackage) != null) {
      return {
        name: objs.choosenInfo.choosenPackage.name ?? 'Selected Package',
        price: objs.choosenInfo.choosenPackage.price ?? 0
      }
    }

    if (((objs?.choosenInfo?.choosenDataMM) != null) || ((objs?.choosenInfo?.choosenCallMM) != null)) {
      const dataPrice = objs.choosenInfo.choosenDataMM?.price ?? 0
      const callPrice = objs.choosenInfo.choosenCallMM?.price ?? 0
      return {
        name: translate('orderSummary.customPackageName'),
        price: dataPrice + callPrice
      }
    }

    return { name: 'Package', price: 0 }
  }, [objs?.choosenInfo])

  const mainServices = useMemo(() => {
    const services: any[] = []
    if ((objs?.choosenInfo?.choosenDataMM) != null) {
      services.push({
        name: objs.choosenInfo.choosenDataMM.name ?? 'Data',
        price: objs.choosenInfo.choosenDataMM.price ?? 0,
        icon: 'data'
      })
    }
    if ((objs?.choosenInfo?.choosenCallMM) != null) {
      services.push({
        name: objs.choosenInfo.choosenCallMM.name ?? 'Calls',
        price: objs.choosenInfo.choosenCallMM.price ?? 0,
        icon: 'call'
      })
    }
    return services
  }, [objs?.choosenInfo])

  const additionalServices = useMemo(() => {
    const services: any[] = []
    const info = objs?.choosenInfo

    if ((info?.choosenSmsMM) != null) {
      services.push({
        name: info.choosenSmsMM.name ?? 'SMS Service',
        price: info.choosenSmsMM.price ?? 0,
        img: info.choosenSmsMM.img ?? null,
        icon: 'sms'
      })
    }
    if ((info?.choosenVideoStreamingMM) != null) {
      services.push({
        name: info.choosenVideoStreamingMM.name ?? 'Video Streaming',
        price: info.choosenVideoStreamingMM.price ?? 0,
        icon: 'entertainment'
      })
    }
    if ((info != null) && Array.isArray(info.choosenEntertainmentMM)) {
      info.choosenEntertainmentMM.forEach(ent => {
        services.push({
          name: ent.name ?? 'Entertainment Service',
          price: ent.price ?? 0,
          icon: 'entertainment',
          img: ent.img ?? null
        })
      })
    }
    return services
  }, [objs?.choosenInfo])

  const totalPrice = useMemo(() => {
    const addTotal = additionalServices.reduce((s, i) => s + i.price, 0)
    return packageInfo.price + addTotal
  }, [packageInfo.price, additionalServices])

  const creditLimitPrice = totalPrice + 100000

  const getServiceIcon = (type: string) => {
    switch (type) {
      case 'data':
        return (theme === 'dark'
          ? '/icons/package_data_dark.svg'
          : '/icons/package_data.svg')
      case 'call':
        return (theme === 'dark'
          ? '/icons/package_call_dark.svg'
          : '/icons/package_call.svg')
      case 'sms':
        return (theme === 'dark'
          ? '/icons/package_sms_dark.svg'
          : '/icons/package_sms.svg')
      case 'video':
        return '/icons/entertainment.svg'
      default:
        return '/icons/service.svg'
    }
  }

  const handleCreditLimitToggle = async (): Promise<void> => {
    if (usageLimit) {
      setLoading(true)
      try {
        const res = await numberv2.saveCreditLimitSettings(objs?.instanceId ?? '', usageLimit, creditLimitPrice)

        if (res?.success) {
          onConfirm()
        } else {
          showError(translate('error'))
        }
      } catch (error) {
        showError(translate('error'))
        console.error('creditLimitToggle error:', error)
        onConfirm()
      } finally {
        setLoading(false)
      }
    } else {
      onConfirm()
    }
  }

  return (
    <div className='w-full max-w-md mx-auto bg-surfaceInverse rounded-xl md:shadow h-full md:h-[75vh] flex flex-col'>
      <div className='flex-grow px-4 py-6 sm:px-6 overflow-auto'>
        <div className='bg-surfaceLow rounded-xl p-4 mb-4'>
          <div className='flex justify-between items-center mb-4'>
            <p className='text-sm text-textMid'>
              {translate('orderSummary.selectedNumberLabel')}
            </p>
            <p className='text-sm font-bold text-textHigh'>{objs?.choosenNumber}</p>
          </div>

          <div className='border border-secondaryMidStroke rounded-xl p-1 bg-surfaceInverse'>
            <div className='flex justify-between items-start border-b border-secondarySoftLow pb-1'>
              <span className='text-lg font-bold text-textHigh'>{packageInfo.name}</span>
            </div>

            {mainServices.length > 0
              ? (
                <div className='space-y-3'>
                  <div className='flex flex-col divide-y divide-secondarySoftLow'>
                    <div className='flex justify-between items-center py-2'>
                      <div className='flex flex-col gap-1 justify-center'>
                        {mainServices.map((service, index) => (
                          <div key={index} className='flex items-center gap-1'>
                            <Image
                              width={20}
                              height={20}
                              src={getServiceIcon(service.icon)}
                              alt={service.icon}
                              className='w-5 h-5 object-contain'
                            />
                            <span
                              className={`leading-tight ${service.icon === 'call'
                                ? 'text-xs font-medium text-textHigh'
                                : 'text-sm font-semibold text-textHigh'
                                }`}
                            >
                              {service.name}
                              {service.icon === 'call' && (
                                <span className='text-xs text-textHigh ml-1'>
                                  {translate('mymobi.unlimited')}
                                </span>
                              )}
                              {service.icon === 'data' && (
                                <span className='text-xs text-textHigh ml-1'>
                                  {translate('mymobi.data')}
                                </span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className='flex flex-col items-end justify-center leading-tight'>
                        <p className='text-xs text-textHigh font-semibold leading-none mb-2'>
                          {translate('orderSummary.perMonthLabel')}
                        </p>
                        <p className='text-sm font-semibold text-textHigh leading-none'>
                          {moneyFormatter(packageInfo.price)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                )
              : (
                <p className='text-sm text-textMid italic'>
                  {translate('orderSummary.noMainService')}
                </p>
                )}
          </div>

          {additionalServices.length > 0 && (
            <div className='mb-6 px-1'>
              <h4 className='text-sm font-medium text-textMid mb-3 mt-2'>
                {translate('orderSummary.additionalServices')}
              </h4>
              <div className='space-y-3'>
                {additionalServices.map((service, index) => (
                  <div key={index} className='flex items-center justify-between'>
                    <div className='flex items-center gap-3'>
                      {service.img != null
                        ? (
                          <Image
                            width={24}
                            height={24}
                            src={service.img}
                            alt={service.name ?? 'service'}
                            className='w-6 h-6 rounded-md object-contain'
                          />
                          )
                        : (
                          <Image
                            width={16}
                            height={16}
                            src={getServiceIcon(service.icon)}
                            alt={service.icon}
                            className='w-4 h-4 opacity-80'
                          />
                          )}
                      <span className='text-sm text-textHigh font-medium'>
                        {service.name}
                      </span>
                    </div>
                    <span className='text-sm font-medium text-textMid'>
                      {moneyFormatter(service.price)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className='border border-secondaryMidStroke rounded-2xl bg-surfaceInverse p-4 flex flex-col gap-4 shadow-sm'>
          <div className='flex items-center justify-between'>
            <div className='w-10 h-10 flex items-center justify-center rounded-xl border border-secondarySoftLow bg-surfaceLow'>
              <img
                src='/icons/usage-limit.svg'
                alt='usage-limit'
                className='w-5 h-5'
              />
            </div>
            <label className='relative inline-flex items-center cursor-pointer'>
              <input
                type='checkbox'
                checked={usageLimit}
                onChange={() => setUsageLimit(!usageLimit)}
                className='sr-only peer'
              />
              <div className='w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-primaryMain transition-colors' />
              <div className='absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5' />
            </label>
          </div>

          {!usageLimit
            ? (
              <>
                <div className='flex flex-col'>
                  <span className='text-base font-semibold text-textHigh'>
                    {translate('orderSummary.usageLimitTitle')}
                  </span>
                  <span className='text-sm text-textMid leading-snug'>
                    {translate('orderSummary.usageLimitDescription')}
                  </span>
                </div>
                <div className='w-full border-t border-secondarySoftLow' />
              </>
              )
            : (
              <div className='border border-secondarySoft bg-secondarySoftLow rounded-xl py-2 px-3 flex items-center justify-between'>
                <p className='text-sm font-medium text-textMid'>
                  {translate('orderSummary.usageLimitActiveLabel')}
                </p>
                <p className='text-sm font-medium text-textHigh'>{moneyFormatter(creditLimitPrice)}</p>
              </div>
              )}

          <DarkButton
            type='outlined'
            fullwidth
            size='sm'
            classNames='font-semibold'
            onClick={() => setShowPopup(true)}
          >
            {translate('orderSummary.detailsButton')}
          </DarkButton>

          {showPopup && (
            <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]'>
              <div className='bg-surfaceInverse border border-secondaryMidStroke rounded-xl p-6 max-w-md w-[90%] shadow-lg'>
                <h2 className='text-base font-semibold text-textHigh mb-3'>
                  {translate('orderSummary.usageLimitTitle')}
                </h2>
                <p className='text-sm text-textMid mb-4'>
                  {translate('orderSummary.usageLimitBody')}
                </p>
                <h2 className='text-base font-semibold text-textHigh mb-3'>
                  {translate('orderSummary.activationMandatoryTitle')}
                </h2>
                <p className='text-sm text-textMid mb-4'>
                  {translate('orderSummary.activationMandatoryBody')}
                </p>
                <h2 className='text-base font-semibold text-textHigh mb-3'>
                  {translate('orderSummary.usageLimitReminderTitle')}
                </h2>
                <p className='text-sm text-textMid mb-6'>
                  {translate('orderSummary.usageLimitReminderBody')}
                </p>
                <DarkButton
                  fullwidth
                  size='sm'
                  onClick={() => setShowPopup(false)}
                >
                  {translate('orderSummary.closeButton')}
                </DarkButton>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='fixed md:sticky bottom-0 rounded-b-xl right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
        <div className='md:w-96 w-full flex'>
          <DarkButton fullwidth onClick={handleCreditLimitToggle} disabled={loading}>
            {translate('continue')}
          </DarkButton>
        </div>
      </div>
    </div>
  )
}

export default OrderSummary
